import AdDisclaimer from "../addisclaimer/AdDisclaimer";
import DisclosurePage from "../DisclosureComponent/DisclosureComponent";
import DoDont from "../dodont/DoDont";
import LegalisclDaimer from "../legaldisclaimer/LegalisclDaimer";
import PrivacyPolicy from "../privacypolicy/PrivacyPolicy";
import RefundPolicy from "../refundpolicy/RefundPolicy";
import { Helmet } from "react-helmet-async";

/* ---------------------------------------------------------------------
   Design tokens — one place to control color, brightness & contrast
   -----------------------------------------------------------------
   --navy       #0F3D5C   headings / table header background / brand
   --accent     #C08A2E   muted gold accent (numbering, top border)
   --ink        #1B2430   primary body text (near-black, not pure black)
   --ink-soft   #4A5568   secondary / muted text
   --bg         #FFFFFF   page background
   --bg-soft    #F6F8FA   card & zebra-row background
   --line       #E2E5EA   hairline borders / dividers
   --ok-bg      #EEF5EF   "DO" column tint
   --ok-text    #2E6B45   "DO" header background
   --danger-bg  #FBEEEE   "DON'T" column tint
   --danger-text#8A2E2E   "DON'T" header background
------------------------------------------------------------------------ */

const globalStyles = `
  html, body, #root {
    background-color: #FFFFFF !important;
  }

  .tc-page {
    --navy: #0F3D5C;
    --accent: #C08A2E;
    --ink: #1B2430;
    --ink-soft: #4A5568;
    --bg: #FFFFFF;
    --bg-soft: #FFFFFF;
    --line: #E2E5EA;
    --ok-bg: #EEF5EF;
    --ok-text: #2E6B45;
    --danger-bg: #FBEEEE;
    --danger-text: #8A2E2E;

    width: 100%;
    box-sizing: border-box;
    background: #FFFFFF !important;
    color: var(--ink);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    padding: 24px 0 60px;
  }

  .tc-inner {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px;
    box-sizing: border-box;
  }

  .tc-masthead {
    border-bottom: 3px solid var(--navy);
    padding-bottom: 20px;
    margin-bottom: 28px;
  }

  .tc-eyebrow {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    margin: 0 0 8px;
  }

  .tc-h1 {
    font-size: clamp(26px, 4vw, 38px);
    font-weight: 800;
    color: var(--navy);
    letter-spacing: -0.02em;
    margin: 0 0 8px;
  }

  .tc-subtitle {
    font-size: 15px;
    color: var(--ink-soft);
    margin: 0;
  }

  .tc-toc {
    background: var(--bg-soft);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 20px 24px;
    margin-bottom: 28px;
    width: 100%;
    box-sizing: border-box;
  }

  .tc-toc-title {
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--navy);
    margin: 0 0 12px;
  }

  .tc-toc ol {
    columns: 3;
    column-gap: 28px;
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
  }

  .tc-toc li { margin-bottom: 6px; break-inside: avoid; }

  .tc-toc a {
    color: var(--ink-soft);
    text-decoration: none;
    border-bottom: 1px dotted transparent;
  }
  .tc-toc a:hover { color: var(--navy); border-bottom-color: var(--accent); }

  @media (max-width: 900px) {
    .tc-toc ol { columns: 2; }
  }

  @media (max-width: 560px) {
    .tc-toc ol { columns: 1; }
  }

  .tc-card {
    width: 100%;
    box-sizing: border-box;
    background: var(--bg);
    border-radius: 20px;
    padding: clamp(20px, 4vw, 40px);
    margin-bottom: 24px;
    position: relative;
    border: 1px solid var(--line);
    box-shadow: 0 16px 30px -12px rgba(15, 61, 92, 0.10);
    overflow: hidden;
  }

  .tc-card-top-border {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 6px;
    background: linear-gradient(90deg, var(--navy) 0%, var(--accent) 50%, var(--navy) 100%);
  }

  .tc-card-badge {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: var(--accent);
    display: block;
    margin-bottom: 6px;
  }

  .tc-card-title {
    font-size: clamp(20px, 3vw, 28px);
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--navy);
    margin: 0 0 8px;
  }

  .tc-card-divider {
    width: 60px;
    height: 4px;
    border-radius: 2px;
    background: var(--bg-soft);
    position: relative;
    margin-bottom: 20px;
  }

  .tc-card-divider::after {
    content: "";
    position: absolute;
    left: 0; top: 0;
    width: 30%;
    height: 100%;
    background: var(--accent);
    border-radius: 2px;
  }

  .tc-card-body {
    color: var(--ink);
    line-height: 1.8;
    font-size: clamp(14px, 1.6vw, 16px);
  }

  .tc-section {
    margin-bottom: 36px;
    scroll-margin-top: 20px;
  }

  .tc-h2 {
    font-size: 21px;
    font-weight: 800;
    color: var(--navy);
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--line);
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  .tc-h2 .tc-num {
    font-size: 14px;
    font-weight: 700;
    color: var(--accent);
  }

  .tc-h3 {
    font-size: 17px;
    font-weight: 700;
    color: var(--navy);
    margin: 22px 0 8px;
  }

  .tc-h4 {
    font-size: 14px;
    font-weight: 700;
    color: var(--ink-soft);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 18px 0 8px;
  }

  .tc-card-body p { margin: 0 0 14px; }
  .tc-card-body ul, .tc-card-body ol {
    margin: 0 0 14px;
    padding-left: 22px;
  }
  .tc-card-body li { margin-bottom: 6px; }
  .tc-card-body a { color: var(--navy); font-weight: 600; text-decoration: underline; }
  .tc-card-body strong { color: var(--ink); }

  .tc-note {
    background: var(--bg-soft);
    border-left: 3px solid var(--accent);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--ink-soft);
    border-radius: 0 8px 8px 0;
    margin: 0 0 16px;
  }

  /* Tables */
  .tc-table-wrap {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--line);
    border-radius: 12px;
    margin: 16px 0 8px;
  }

  table.tc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
  }

  table.tc-table th {
    text-align: left;
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 14px 16px;
    background: var(--navy);
    color: #ffffff;
  }

  table.tc-table th.tc-th-num { width: 48px; text-align: center; }
  table.tc-table th.tc-th-do { background: var(--ok-text); }
  table.tc-table th.tc-th-dont { background: var(--danger-text); }

  table.tc-table td {
    padding: 14px 16px;
    vertical-align: top;
    border-bottom: 1px solid var(--line);
    color: var(--ink);
  }

  table.tc-table tr:last-child td { border-bottom: none; }

  table.tc-table tbody tr:hover td {
    filter: brightness(0.97);
  }

  table.tc-table td.tc-num-cell {
    text-align: center;
    font-weight: 700;
    color: var(--ink-soft);
    background: var(--bg-soft);
  }

  table.tc-table td.tc-do-cell { background: var(--ok-bg); }
  table.tc-table td.tc-dont-cell { background: var(--danger-bg); }

  .tc-empty { color: var(--ink-soft); opacity: 0.45; }

  @media (max-width: 640px) {
    .tc-inner { padding: 0 16px; }
  }
`;

/* --------------------------- Reusable pieces --------------------------- */

function Section({ id, num, title, children }) {
  return (
    <section id={id} className="tc-section">
      <h2 className="tc-h2">
        <span className="tc-num">{num}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function DoDontTable({ rows }) {
  return (
    <div className="tc-table-wrap">
      <table className="tc-table">
        <thead>
          <tr>
            <th className="tc-th-num">#</th>
            <th className="tc-th-do">Do's</th>
            <th className="tc-th-dont">Don'ts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="tc-num-cell">{i + 1}</td>
              <td className="tc-do-cell">
                {r.do ? r.do : <span className="tc-empty">—</span>}
              </td>
              <td className="tc-dont-cell">
                {r.dont ? r.dont : <span className="tc-empty">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PolicyCard({ title, children }) {
  return (
    <div className="tc-card">
      <div className="tc-card-top-border" />
      <span className="tc-card-badge">Official Policy</span>
      <h2 className="tc-card-title">{title}</h2>
      <div className="tc-card-divider" />
      <div className="tc-card-body">{children}</div>
    </div>
  );
}

/* ------------------------------ Table data ------------------------------ */

const beforeTradingRows = [
  {
    do: "Assess the risk-return profile of the investment as well as the liquidity and safety aspects before making and/or acting upon your investment decision.",
    dont: "Don't trade in any product without knowing and/or understanding the associated risks and rewards.",
  },
  {
    do: "Make investments based on sound reasoning, after taking into account all publicly available information. You may access the websites of Companies and Regulators for this information.",
    dont: "Don't get influenced by information that does not originate from an appropriate source.",
  },
  {
    do: "Make all payments in the form of cheque/online transfer in the name Option bricks and Research Services only.",
    dont: "Don't get influenced into buying fundamentally unsound companies (penny stocks) based on sudden spurts in trading volumes or prices or non-authentic favourable looking articles/stories.",
  },
  { do: null, dont: "Don't match/synchronize trades with any other person for any thinly traded shares." },
  {
    do: null,
    dont: "Don't indulge in any trading activity which results in the disturbance of market equilibrium in any manner including manipulation of the price of any scripts.",
  },
  { do: null, dont: "Don't get carried away by luring advertisements, if any." },
  {
    do: null,
    dont: "Don't make a cash payment or issue a cheque in the name of any employee of Option bricks and Research Services or any other person in respect of the transactions.",
  },
  { do: null, dont: "Don't issue blank/bearer cheques to anyone." },
];

const generalPracticeRows = [
  {
    do: "Give unambiguous instructions to your broker while placing orders over the phone with your broker.",
    dont: "In any circumstance, do not reveal your login credentials to any other person, not even an Option bricks and Research Services representative.",
  },
  {
    do: "You will be solely responsible for all transactions effected by internet trading. Therefore, change your password every fortnight.",
    dont: null,
  },
  {
    do: "Be aware of the risk associated with the asset classes you are trading in the market and margin calls on them. In case of inadequate margin or non-payment of margin in your account, your positions will be squared off by the Risk Management team of your broker.",
    dont: null,
  },
  {
    do: "Maintain adequate margins at all points of time. In case of margin shortfall or delay in payments, your outstanding position will be squared up by your broker.",
    dont: null,
  },
];

const stayProtectedRows = [
  {
    do: "Forward suspicious emails asking for sensitive information to compliance@optionbricks.in.",
    dont: "Do not reveal your personal information such as Customer IDs, Passwords, and One Time Passwords (OTP) to anybody over the phone, on the internet, or via email.",
  },
  {
    do: "Write to us at compliance@optionbricks.in, if you notice a duplicate Option bricks and Research Services or webpage.",
    dont: "Do not open or click on attachments included in emails from organizations or people you don't know.",
  },
  {
    do: "Call us on +919082280240 right away, if you've provided sensitive account information to an unauthorized person, or to report a fraud.",
    dont: "Do not enable the 'Auto Run' feature in Windows systems.",
  },
  {
    do: "Install and update good-quality antivirus software.",
    dont: "Use an outdated web browser or one that isn't up to date.",
  },
  {
    do: "Use only devices (like USBs, external hard drives, and smartphones) that you completely trust.",
    dont: "Do not divulge personal information on social media.",
  },
  {
    do: "Exercise caution when using the internet in public places such as internet cafes and airport lounges.",
    dont: null,
  },
  {
    do: "Type the entire Option bricks and Research Services address (http://optionbricks.in/) into your browser to avoid being misdirected to a bogus site when you use the search option.",
    dont: "Do not disclose personal or business information via a website link attached to an email.",
  },
  {
    do: null,
    dont: "Do not trust a site appearing to belong to Option bricks and Research Services if it doesn't start with the 'HTTPS' prefix and doesn't have a padlock symbol.",
  },
];

const toc = [
  ["terms", "Terms & Conditions"],
  ["disclaimers", "Other Disclaimers"],
  ["report-disclaimer", "Disclaimer for Reports"],
  ["liability-limits", "Liability Limitations"],
  ["reasonableness", "Reasonableness"],
  ["other-parties", "Other Parties"],
  ["unenforceable", "Unenforceable Provisions"],
  ["jurisdiction-1", "Jurisdiction"],
  ["privacy-policy-1", "Privacy Policy"],
  ["privacy-policy-2", "Privacy Policy (Website Terms)"],
  ["registration", "Registration, Enrolment & Termination"],
  ["license", "License"],
  ["security", "Security"],
  ["service-delays", "Service Delays"],
  ["products", "Products Offered on the Website"],
  ["agreements", "You Hereby Agree That"],
  ["liability", "Liability"],
  ["third-party-links", "Links to Third-Party Sites"],
  ["indemnification", "Indemnification"],
  ["entire-agreement", "Entire Agreement"],
  ["jurisdiction-2", "Jurisdiction (Governing Law)"],
  ["ethics-copyright", "Code of Ethics & Copyright"],
  ["before-trading", "Before You Start Trading"],
  ["fund-transfer", "Secure Fund Transfer"],
  ["general-practice", "General Practice While You Trade"],
  ["stay-protected", "How to Stay Protected at All Times"],
  ["customer-awareness", "Customer Awareness of Security"],
];

/* --------------------------------- Page --------------------------------- */

export default function TAndC() {
  return (
    <div className="tc-page" style={{ backgroundColor: "#FFFFFF" }}>
    <Helmet>
  <title>Term and Conditions | Option Bricks</title>

</Helmet>
      <style>{globalStyles}</style>
      <div className="tc-inner" style={{ backgroundColor: "#FFFFFF" }}>
        <header className="tc-masthead">
          <p className="tc-eyebrow">SEBI-Registered Research Entity</p>
          <h1 className="tc-h1">Terms &amp; Conditions and Policies</h1>
          <p className="tc-subtitle">
            Option bricks and Research Services — please read this document in full before
            subscribing to any service on{" "}
            <a href="http://optionbricks.in/">optionbricks.in</a>.
          </p>
        </header>

        <nav className="tc-toc" aria-label="Table of contents">
          <p className="tc-toc-title">On this page</p>
          <ol>
            {toc.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`}>{label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <PolicyCard title="Terms & Conditions">
          <Section id="terms" num="01" title="Terms & Conditions">
            <p>
              Option bricks" or "Research Entity" is regulated by the Securities and
              Exchange Board of India ("SEBI") and is licensed to carry on the business of
              share trading research providing service and related activities. The term
              "you" refers to the user or viewer of our website. Before subscribing our any
              of the services we advise you to please visit our website{" "}
              <a href="http://optionbricks.in/">http://optionbricks.in/</a> and read all
              terms &amp; conditions carefully. By subscribing our any of the services you
              agree to be bound by the terms &amp; conditions. We may change these terms and
              conditions at any time without prior information/intimation.
            </p>
            <p>
              Your continuous use of <a href="http://optionbricks.in/">http://optionbricks.in/</a>{" "}
              means that you accept any new or modified terms and conditions that we come up
              with. Please re-visit the 'Terms &amp; Conditions' link on our website from
              time to time to stay abreast of any changes that we may introduce.
              Investment/trading in Share Market are subject to market and other risks and
              there is no assurance or guarantee that the objectives of any Services will be
              achieved.
            </p>
            <p>
              Traders/Investors in the services are not being offered any
              guaranteed/assured returns. Past performance of the services does not
              indicate/guarantee future performance or return for any of the services. We do
              not have any refund &amp; cancellation policy. All sales are final. Before
              subscriptions ensure that our products and services will meet your needs
              without the need to purchase, there will be absolutely no refund &amp;
              cancellations. We do not offer refunds on subscriptions that have already been
              taken. By registering for service on the website{" "}
              <a href="http://optionbricks.in/">http://optionbricks.in/</a> you have accepted
              all the terms and conditions.
            </p>
          </Section>

          <Section id="disclaimers" num="02" title="Other Disclaimers">
            <p>
              Without prejudice to the generality of the foregoing paragraph, Option bricks
              Share Service and its owner, directors, employees, affiliates and associates
              state that: The information analysis given in the research recommendation or
              research report is purely an interpretation / view of the author/research
              analyst and Option bricks and its owner, directors, employees, affiliates and
              associates and any of its representatives shall not be liable or responsible in
              any manner whatsoever for the trades executed by the customers on the basis of
              this recommendation or reports. Any comparative indices that are made available
              on the Website are mere indicators and are in no manner meant to reflect or
              influence the buying decision of the customers. Option bricks Service and its
              owner, directors, employees, affiliates and associates and or the Option bricks
              do not in any manner whatsoever verify or vouch for the accuracy or authenticity
              of the results. While doing fund transfer, the respective bank shall be
              responsible for transfer of funds during payment for research services on the
              Website and Option bricks Service and its owner, directors, employees,
              affiliates and associates and or the Option bricks shall not be responsible or
              liable in any manner whatsoever for the late payments or losses incurred on
              account of the same. Nothing on this website constitutes, or is meant to
              constitute, advice of any kind. If you require advice in relation to any
              investments, legal, financial, medical matters or any other matters, you should
              consult an appropriate professional.
            </p>
          </Section>

          <Section id="report-disclaimer" num="03" title="Disclaimer for Reports">
            <p>
              The documents/reports/research analysis available on the Website or
              published/produced by Option bricks does not constitute an offer or
              solicitation for the purchase or sale of any Security / financial instrument or
              as an official confirmation of any transaction. Stock Option bricks Service and
              its owner, directors, employees, affiliates and associates and or the Option
              bricks shall not be in any way responsible for any loss or damage that may arise
              to any person from any inadvertent error in the information contained in any of
              the research recommendation or research report/alert. The
              documents/reports/research analyses are provided for assistance only and is not
              intended to be and must not alone be taken as the basis for any decision. The
              user assumes the entire risk of any use made of this information. This
              information is strictly confidential and is being furnished to you solely for
              your information. This information should not be reproduced or redistributed or
              passed on directly or indirectly in any form to any other person or published,
              copied, in whole or in part, for any purpose. The distribution of this document
              in certain jurisdictions may be restricted by law, and persons in whose
              possession this document comes, should inform themselves about and observe, any
              such restrictions.
            </p>
            <p>
              The information given in this document is as of the date and time of the
              documents/reports/research analysis and there can be no assurance that future
              results or events will be consistent with this information. The information is
              subject to change without any prior notice. Option bricks Service and its
              owner, directors, employees, affiliates and associates and or the Stock Option
              bricks reserves the right to make modifications and alterations to this
              statement as may be required from time to time. However, Stock Option bricks
              and its owner, directors, employees, affiliates and associates and /or the
              Option bricks are under no obligation to update or keep the information current.
              Neither Option bricks Service nor any of its owners, directors, employees,
              agents or representatives shall be liable for any damages whether direct,
              indirect, special or consequential including lost revenue or lost profits or
              lost capital that may arise from or in connection with the use of the
              information. For data reference to any third party in this material no such
              party will assume any liability for the same.
            </p>
            <p>
              All layout, design, original artwork, concepts and other Intellectual
              Properties, remains the property and copyright of Option bricks and may not be
              used in any form or for any purpose whatsoever by any party without the express
              written permission of the copyright holders. This is a private website owned by
              Option bricks. Unauthorized scanning of vulnerabilities and exploitation of the
              same is strictly prohibited. If you have been found to do so, Option bricks may
              bring Civil and/or Criminal action and/or legal proceedings against you which
              shall be entirely at your costs and consequences.
            </p>
          </Section>

          <Section id="liability-limits" num="04" title="Liability Limitations">
            <p>
              Option bricks firm and its owner, directors, employees, affiliates and
              associates and or the Option bricks firm will not be liable to you (whether
              under the law of contract, the law of torts or other Option bricks under any
              other law) in relation to the contents of, or use of, or other Option bricks in
              connection with, this Website:
            </p>
            <p>
              For any indirect, special or consequential loss; or for any business losses,
              loss of revenue, income, profits or capital or anticipated savings, loss of
              contracts or business relationships, loss of reputation or goodwill, or loss or
              corruption of information or data.
            </p>
            <p>
              These limitations of liability apply even if Option bricks firm and its owner,
              directors, employees, affiliates and associates and or the Option bricks have
              been expressly advised of the potential loss.
            </p>
          </Section>

          <Section id="reasonableness" num="05" title="Reasonableness">
            <p>
              By using this Website, you agree that the exclusions and limitations of
              liability set out in this website disclaimer are reasonable. If you do not
              think they are reasonable, you must not use this Website.
            </p>
          </Section>

          <Section id="other-parties" num="06" title="Other Parties">
            <p>
              You accept that Option bricks firm has an interest in limiting the personal
              liability of its owner, officers, employees, directors, affiliates and group
              companies. You agree that you will not bring any claim personally against
              Option bricks firm and its owner, directors, employees, affiliates and
              associates and or the Option bricks Services firm in respect of any losses you
              suffer in connection with the website.
            </p>
            <p>
              Without prejudice to the foregoing paragraph, you agree that the limitations of
              warranties and liability set out in this website disclaimer will protect Option
              bricks firm and its owner, directors, employees, affiliates and associates and
              or the Option bricks Services firm.
            </p>
          </Section>

          <Section id="unenforceable" num="07" title="Unenforceable Provisions">
            <p>
              If any provision of this Website disclaimer is, or is found to be,
              unenforceable under applicable law, that will not affect the enforceability of
              the other provisions of this Website disclaimer.
            </p>
          </Section>

          <Section id="jurisdiction-1" num="08" title="Jurisdiction">
            <p>
              Your use of this website and any dispute arising out of such use of the website
              is subject to the laws of India. You hereby consent to the exclusive
              jurisdiction and venue of courts in Mumbai or Navi Mumbai, Maharashtra, India in
              all disputes arising out of or relating to the use of this Website. Use of this
              website is unauthorized in any jurisdiction that does not give effect to all
              provisions of these terms and conditions, including without limitation this
              paragraph.
            </p>
          </Section>

          <Section id="privacy-policy-1" num="09" title="Privacy Policy">
            <p>
              Your privacy is important to Option bricks. This privacy statement provides
              information about the personal information that Option bricks Services
              collects, and the ways in which Option bricks uses that personal information.
            </p>

            <h3 className="tc-h3">Personal information collection</h3>
            <p>Option bricks may collect and use the following kinds of personal information:</p>
            <ul>
              <li>Information about your use of this website.</li>
              <li>Information that you provide for the purpose of registering with Option bricks ("the Website").</li>
              <li>Information about transactions carried out over this Website.</li>
              <li>Information that you provide for the purpose of subscribing to the Website services.</li>
              <li>Any other information that you send to Option bricks.</li>
            </ul>

            <h3 className="tc-h3">Using personal information</h3>
            <p><strong>Option bricks may use your personal information to: –</strong></p>
            <ul>
              <li>Administer this Website.</li>
              <li>Personalize the Website for you.</li>
              <li>Enable your access to and use of the Website services.</li>
              <li>Publish information about you on the Website.</li>
              <li>Send to your products that you purchase.</li>
              <li>Supply to your services that you purchase.</li>
              <li>Send you statements and invoices.</li>
              <li>Collect payments from you and</li>
              <li>Send you marketing communications</li>
            </ul>
            <p>
              Where Option bricks may disclose your personal information to its
              employees/agents or sub-contractors for the above purposes, the agent or
              sub-contractor in question will be obligated to use that personal information in
              accordance with the terms of this privacy statement. Option bricks shall or its
              agents and/or sub-contractors may use your personal information to make contact
              with you for promotion of products/schemes/Services etc. of Option bricks or of
              its partners from time to time. You hereby agree and understand that such
              promotional calls shall be made to you unless you specifically convey to Option
              bricks Services to not call you further for such promotions, schemes, services
              etc.
            </p>
            <p>
              In addition to the disclosures reasonably necessary for the purposes identified
              elsewhere above, Option bricks may disclose your personal information to the
              extent that it is required to do so by law, in connection with any legal
              proceedings or prospective legal proceedings, and in order to establish,
              exercise or defend its legal rights.
            </p>

            <h3 className="tc-h3">Securing Your Data</h3>
            <p>
              Option bricks will take reasonable technical and organizational precautions to
              prevent the loss, misuse or alteration of your personal information. Option
              bricks will store all the personal information you provide on its secure
              servers. Information relating to electronic transactions entered into via this
              Website will be protected by encryption technology.
            </p>

            <h3 className="tc-h3">Cross-border data transfers</h3>
            <p>
              Information that Option bricks collects may be stored and processed in and
              transferred between any of the countries in which Option bricks Services
              operates (if at all) to enable the use of the information in accordance with
              this privacy policy. In addition, personal information that you submit for
              publication on the website will be published on the internet and may be
              available around the world. You agree to such cross-border transfers of
              personal information.
            </p>

            <h3 className="tc-h3">Updating this statement</h3>
            <p>
              Option bricks may update this privacy policy by posting a new version on this
              Website. You should check this page occasionally to ensure you are familiar with
              any changes.
            </p>

            <h3 className="tc-h3">Other websites</h3>
            <p>
              This website contains links to other websites. Option bricks is/are not
              responsible for the privacy policies or practices of any third party.
            </p>
            <div className="tc-note">
              If you have any questions about this privacy policy or treatment of your
              personal information, please write to by email:{" "}
              <a href="mailto:compliance@optionbricks.in">compliance@optionbricks.in</a>.
            </div>
          </Section>

          <Section id="privacy-policy-2" num="10" title="Privacy Policy (Website Terms)">
            <p>
              By visiting our site "
              <a href="http://optionbricks.in/"><em>http://optionbricks.in/</em></a>
              " ("Website") you ("you" refers to the user or viewer of the Website) are
              agreeing to be bound by the following terms and conditions and the Privacy
              Policy on the Website. We may change these terms and conditions at any time. By
              using this Website or any utility in this web site directly or indirectly you
              abide by all terms and conditions mentioned herein and that you accept any new
              or modified terms and conditions that we come up with. If you do not agree to
              any of the terms mentioned herein, you should exit the site immediately.
            </p>
          </Section>

          <Section id="registration" num="11" title="Registration / Enrolment and Termination">
            <p>
              By registering for this facility you understand that Option bricks firm and its
              owner, directors, employees and associates (hereinafter singly and jointly
              referred to as "Option bricks") reserves the right, in its sole discretion, to
              deny you access to this Website or any portion thereof without notice for the
              following reasons (a) any unauthorized access or use by you (b) if you assign or
              transfer (or attempt the same) any rights granted to you under this User
              Agreement or any other agreement that you may have entered with Option bricks;
              (c) if you violate any of the other terms and conditions of this User Agreement.
              Termination or cancellation of this Agreement shall not affect any right or
              relief to which Option bricks may be entitled, at law or in equity. Upon
              termination of this User Agreement, all rights granted to you will terminate
              and revert to Option bricks.
            </p>
          </Section>

          <Section id="license" num="12" title="License">
            <p>
              Option bricks, hereby grants you a limited, non-exclusive, non-assignable and
              non-transferable license to access to the Website provided and expressly
              conditioned upon your agreement that all such access and use shall be governed
              by all of the terms and conditions set forth in this User Agreement.
            </p>
          </Section>

          <Section id="security" num="13" title="Security">
            <p>
              Unauthorized use of this Website and systems, including, but not limited to,
              unauthorized entry into Option bricks's systems, online accounts, Client Desks,
              Partners &amp; Distributor Desks, misuse of passwords or misuse of any other
              information, is strictly prohibited. You may not use this Website in any manner
              that could damage, disable, overburden, or impair this Website or service or
              interfere with any other party's use and enjoyment of this Website or service.
              You may not attempt to gain unauthorized access to any of our site or service and
              computer systems or networks connected to it, through hacking. You agree that
              you will not engage in any activities related to this web site that are contrary
              to the applicable laws or regulations in force.
            </p>
            <p>
              The users have the primary responsibility to keep their login-ids and passwords
              secure and to not display such crucial, important information to any third
              person. In case of any such leak of login-id &amp; password, Option bricks
              cannot be responsible for the loss of such information, or for any act,
              omission, damages, claims, loss of personal information, etc. arising from the
              use of such leakage of login-ids and passwords. Option bricks may, at its own
              discretion, use certain technology on its web sites to collect information from
              visitors and may compile aggregate statistical information about how visitors
              use its web sites, including information relating to the frequency of visits,
              the average length of visits, which pages are viewed during a visit. No
              individually identifiable information is collected for this purpose and Option
              bricks Services does not monitor individual visitor behaviour.
            </p>
          </Section>

          <Section id="service-delays" num="14" title="Service Delays">
            <p>
              Option bricks reserves its right, in its sole discretion, without any obligation
              and without any notice requirement, to change, improve or correct the
              information, materials and descriptions on this web site and to suspend and/or
              deny access to this web site for scheduled or unscheduled maintenance,
              upgrades, improvements or corrections. Option bricks Share Services may
              discontinue or change any service described in or offered on this web site or in
              any modules or parts thereof at any time if deemed fit and proper. Option bricks
              (including its owner, directors, employees, affiliates, agents, representatives
              or subcontractors) shall not be liable for any loss or liability resulting,
              directly or indirectly, from delays or interruptions due to electronic or
              mechanical equipment failures, telephone interconnect problems, defects,
              weather, strikes, walkouts, fire, riots, armed conflicts, acts of war, pandemic
              situations or other like causes. Option bricks shall have no responsibility to
              provide you access to the Website while interruption of the Website is due to
              any such cause shall continue.
            </p>
          </Section>

          <Section id="products" num="15" title="Products Offered on the Website">
            <p>
              With respect to the data from Capital Markets and the products made available
              on the Website by Option bricks including but not limited to stocks, reports
              etc.
            </p>
          </Section>

          <Section id="agreements" num="16" title="You Hereby Agree That">
            <p>You confirm that the details provided by you are true and correct.</p>
            <p>
              You hereby declare that the amount being transferred by you for the purpose of
              subscribing to any of Option bricks research services is derived through
              legitimate sources and is not held or designed for the purpose of contravention
              of any Act, Rules, Regulations or any statute or legislation or any other
              applicable laws or any Notifications, Directions issued by any governmental,
              regulatory or statutory authority from time to time.
            </p>
            <p>
              You agree that Option bricks sources the data from the Capital Markets and
              is/are therefore not responsible and/or should be held liable by you for the
              correctness or accuracy of any information received in that regard from the
              Capital Markets.
            </p>
            <p>
              You agree that the National Stock Exchange ("NSE") data displayed on the site is
              always delayed by five (5) minutes whereas the Bombay Stock Exchange ("BSE")
              data displayed on the site is delayed by fifteen (15) minutes; and technical
              glitches if any, may further delay the data at times and that you will not hold
              Option bricks responsible for any such delays in receiving the data / feeds from
              time to time.
            </p>
            <p>
              You agree that the authenticity of the data received from Capital Markets is not
              guaranteed by Option bricks and that Option bricks Services shall assume no
              responsibility or liability in any manner whatsoever for such data and your
              subsequent trade, action etc. acting upon such data.
            </p>
          </Section>

          <Section id="liability" num="17" title="Liability">
            <p>
              You agree that, Option bricks (Including its owner, officers, directors,
              employees, affiliates, group companies agents, representatives or
              subcontractors) shall not be, in any event, be liable, whether in contract or
              tort, for any direct, special, indirect, consequential or incidental damages or
              other damages of any kind whatsoever arising out of the use or inability to use
              Stock Option bricks Services ("the Website") for any purpose whatsoever even if
              Option bricks Services or any other such party has been advised of the
              possibility thereof. Option bricks and its owner, officers, directors, employees
              and agents shall have no liability in tort, contract, or other Option bricks to
              the user and/or any third party. This limitation on liability includes, but is
              not limited to, the transmission of any viruses which may infect a user's
              equipment, failure of mechanical or electronic equipment or communication
              lines, telephone or other interconnect problems (e.g., you cannot access your
              internet service provider), unauthorized access, theft, operator errors, strikes
              or other labor problems or any force majeure. Option bricks cannot and does not
              guarantee continuous, uninterrupted or secure access to this web site.
            </p>
          </Section>

          <Section id="third-party-links" num="18" title="Links to Third Party Sites">
            <p>
              The links in this site will allow you to leave the Website. The linked sites are
              not under the control of the Website. The Website has not reviewed, nor approved
              these sites and is not responsible for the contents or omissions of any linked
              site or any links contained in a linked site. The inclusion of any linked site
              does not imply endorsement by Option bricks ("the Website").
            </p>
          </Section>

          <Section id="indemnification" num="19" title="Indemnification">
            <p>
              You shall indemnify, defend and hold harmless Option bricks (including its
              owner, officers, directors, employees, affiliates, group companies, agents,
              representatives or subcontractors) from any and all claims and losses imposed
              on, incurred by or asserted as a result of or related to: (a) your access and use
              of Option bricks ("the Website") of Option bricks firm, (b) any non-compliance
              by user with the terms and conditions hereof; or (c) any third party actions
              related to users receipt and use of the information, whether authorized or
              unauthorized. Any clause declared invalid shall be deemed severable and not
              affect the validity or enforceability of the remainder. These terms may only be
              amended in a writing signed by Option bricks. If Option bricks takes action (by
              itself) to enforce any of the provisions of this User Agreement, including
              collection of any amounts due here under, Option bricks shall be entitled to
              recover from you (and you agree to pay), in addition to all sums to which it is
              entitled or any other relief, at law or in equity, reasonable and necessary
              attorney's fees and any costs of any litigation.
            </p>
          </Section>

          <Section id="entire-agreement" num="20" title="Entire Agreement">
            <p>
              This User Agreement constitutes the entire agreement between the parties and is
              inclusive of any other written agreement existing between you and Option bricks
              and Research Services. By using the information on{" "}
              <a href="http://optionbricks.in"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm, you assume full
              responsibility for any gains and losses, financial, emotional, or other Option
              bricks, experienced, suffered, or incurred by you.{" "}
              <a href="http://optionbricks.in"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm does not guarantee
              the accuracy, completeness or timeliness of, or other Option bricks endorse in
              any way, the views, opinions, or recommendations expressed in the Information,
              does not give investment advice and does not advocate the purchase or sale of
              any security or investment by you or any other individual. The Information is
              not intended to provide tax, legal, or investment advice, which you should
              obtain from your professional advisor before making any investment of the type
              discussed in the Information.
            </p>
            <p>
              The Information does not constitute a solicitation by the information providers,{" "}
              <a href="http://optionbricks.in/"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm or any other
              platform for the purchase or sale of securities. The service is provided "as
              is," without warranty of any kind, either express or implied, including without
              limitation, any warranty for information, data, services, uninterrupted access,
              provided through or in connection with the service. Specifically,{" "}
              <a href="http://optionbricks.in/"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm disclaims any
              warranties, including, but not limited to (i) any warranties concerning the
              availability, accuracy, usefulness, or content of information, products or
              services; and (ii) any warranties of title, warranty of non-infringement,
              warranties of merchantability or fitness for a particular purpose.
            </p>
            <p>
              This disclaimer of liability applies to any damages or injury caused by any
              failure of performance, error, omission, interruption, deletion, defect, delay
              in operation or transmission, computer virus, communication line failure, theft
              or destruction or unauthorized access to, alteration of, or use of record,
              whether for breach of contract, tort, negligence, or under any other cause of
              action. Neither{" "}
              <a href="http://optionbricks.in/"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm nor any of its
              owners, employees, agents, successors, assigns, affiliates, group companies, or
              content or service providers shall be liable to you or another third party for
              any direct, indirect, incidental, special or consequential damages arising out
              of the use of service or inability to gain access to or use the service or out
              of any breach of any warranty.
            </p>
            <p>
              Because some countries do not allow the exclusion or limitation of liability
              for consequential or incidental damages, the above limitation may not apply to
              you. In such countries, the respective liability of Option bricks and Research
              Services firm,{" "}
              <a href="http://optionbricks.in/"><em>http://optionbricks.in/</em></a>{" "}
              ("the Website") of Option bricks and Research Services firm, its owner,
              employees, agents, successors, assigns, affiliates, group companies, and content
              or service providers respective liability is limited to the amount provided
              under said law. Further, you agree and understand that all services provided are
              non-refundable and that you should carefully consider whether our services can
              meet your needs. Transactions between users of this site and Option bricks and
              Research Services shall be governed by and construed by the Securities and
              Exchange Board of India ("SEBI") Regulations and laws of India, without regard
              to the laws regarding conflicts of law. Any litigation regarding this agreement
              or any transaction between the customer and Option bricks and Research Services
              or any action at law or in equity arising out of or relating to this agreement
              or transaction shall be filed only in the Competent Courts of Thane, Maharashtra
              and the customer hereby acknowledges, agrees, consents and submits to the
              jurisdiction of such courts to litigate any such action.
            </p>
          </Section>

          <Section id="jurisdiction-2" num="21" title="Jurisdiction">
            <p>
              The terms of this agreement are exclusively based on and subject to Indian law.
              You hereby consent to the exclusive jurisdiction and venue of courts in Thane,
              Maharashtra, India in all disputes arising out of or relating to the use of this
              website. Use of this website is unauthorized in any jurisdiction that does not
              give effect to all provisions of these terms and conditions, including without
              limitation this paragraph.
            </p>
          </Section>

          <Section id="ethics-copyright" num="22" title="Code of Ethics and Conduct">
            <p>Copyright © "2022" Option bricks and Research Services</p>

            <h3 className="tc-h3">Ownership of copyright</h3>
            <p>
              The copyright in this website and the material on this website (including
              without limitation the text, artwork, photographs, images, music, audio
              material, video material, and audio-visual material on this website) is owned by
              Option bricks and Research Services and its licensors.
            </p>

            <h3 className="tc-h3">Copyright license</h3>
            <p>Option bricks and Research Services grant you a non-exclusive royalty-free revocable license to:</p>
            <ul>
              <li>View this website and the material on this website on a computer or mobile device via a web browser.</li>
              <li>Copy and store this website and the material on this website in your web browser cache memory.</li>
              <li>Print pages from this website for your personal non-commercial use.</li>
            </ul>
            <p>
              Option bricks and Research Services do not grant you any other rights about
              this website or the material on this website. In other words, all other rights
              are reserved.
            </p>
            <p>
              For the avoidance of doubt, you must not adapt, edit, change, transform,
              republish, redistribute, broadcast, or show or play in public this website or
              the material on this website (in any form or media) without Option bricks and
              Research Services prior written permission. You may request permission by
              writing to <a href="mailto:compliance@optionbricks.in">compliance@optionbricks.in</a>.
            </p>

            <h3 className="tc-h3">Data mining</h3>
            <p>The automated and/or systematic collection of data from this website is prohibited.</p>

            <h3 className="tc-h3">Enforcement of copyright</h3>
            <p>
              Option bricks and Research Services firm takes the protection of its copyright
              materials very seriously. If Option bricks and Research Services firm discovers
              that you have used our copyright materials in contravention of the license
              above, Option bricks and Research Services may bring legal proceedings against
              you for an injunction to stop you from using those materials and monetary
              damages. You could also be ordered to pay our legal costs. If you become aware
              of any use of Option bricks and Research Services copyright materials that
              contravenes or may contravene the license above, please let us know by email at{" "}
              <a href="mailto:compliance@optionbricks.in">compliance@optionbricks.in</a>.
            </p>

            <h3 className="tc-h3">Infringing material</h3>
            <p>
              If you become aware of any material on the website that you believe infringes
              your or any other person's copyright, please let us know by email at{" "}
              <a href="mailto:compliance@optionbricks.in">compliance@optionbricks.in</a>.
            </p>
          </Section>

          <Section id="before-trading" num="23" title="Before You Start Trading">
            <DoDontTable rows={beforeTradingRows} />
          </Section>

          <Section id="fund-transfer" num="24" title="Secure Fund Transfer">
            <ul>
              <li>
                Payment for all research services should be made in the current account of
                Option bricks and Research Services only. No payment should be made in any
                account of an employee/associate of Stock Option bricks Services and Research
                Services or any other individual in any condition.
              </li>
              <li>In case of payment through Pay Order, Demand Draft, Banker's cheque, NEFT or RTGS; provide the following proof of payment:</li>
              <li>Certificate from the issuing bank on its letterhead or a plain paper with the seal of the issuing bank.</li>
              <li>Certified copy of the requisition slip (portion which is retained by the bank) to issue the instrument.</li>
              <li>Certified copy of the passbook/bank statement for the account debited to issue the instrument.</li>
              <li>Authentication of the bank account number debited and name of the account holder by the issuing bank on the reverse of the instrument.</li>
              <li>Track your ledger regularly to check the credit for payment of funds.</li>
            </ul>
          </Section>

          <Section id="general-practice" num="25" title="General Practise While You Trade">
            <DoDontTable rows={generalPracticeRows} />
          </Section>

          <Section id="stay-protected" num="26" title="How Can You Stay Protected at All Times?">
            <DoDontTable rows={stayProtectedRows} />
          </Section>

          <Section id="customer-awareness" num="27" title="Customer Awareness of Security">
            <p>
              Let's face it, hackers are smart, and they're only getting better by the
              minute. So, while we work hard to keep the bad guys at bay, here's what you can
              do to stay protected at all times.
            </p>

            <h3 className="tc-h4">1. Beware of fraud</h3>
            <ul>
              <li>Don't provide personal information such as Customer IDs, Passwords, and One Time Passwords (OTP) to anybody over the phone, on the internet, or via email.</li>
              <li>Don't open or click on attachments included in emails from organizations or people you don't know.</li>
            </ul>

            <h3 className="tc-h4">2. How to detect a fake email or website</h3>
            <ul>
              <li>A message stating that your account may have been compromised, and asking for personal information.</li>
              <li>An email promising prize money for completing a survey or answering questions that compel you to reveal personal information.</li>
              <li>An email directing you to a fake website designed to look like the real thing.</li>
            </ul>

            <h3 className="tc-h4">3. How fraudsters may try to trick you</h3>
            <ul>
              <li><strong>Phishing</strong> — fake emails that appear to be sent by a credible source to compel you to reveal personal information, which can then be used to commit fraud.</li>
              <li><strong>Vishing</strong> — phone calls by people pretending to work for Option bricks Share Services and Research Services or another legitimate business, asking you to reveal personal information.</li>
              <li><strong>Trojan / Virus</strong> — programs inadvertently installed on your computer to steal your sensitive information, spy on you, or cause a security breach; these are also spread via SMS text messages that urge you to call a number tied to potential fraud.</li>
              <li><strong>Ransomware</strong> — malware that infects your computer, blocks access to your files, and demands money from you to access your files once again.</li>
              <li><strong>Spyware</strong> — software that secretly monitors activity on your computer and reports back to the fraudster, who then uses the information to access your accounts.</li>
            </ul>

            <h3 className="tc-h4">4. How you can secure your passwords</h3>
            <p>Here's how you can keep your passwords from falling into the wrong hands:</p>
            <ul>
              <li>Ignore requests from organizations or individuals requesting you to verify your passwords or PINs, as no reputable financial organization does that.</li>
              <li>Memorize your passwords instead of writing them down, and don't share them with anybody.</li>
              <li>Remember to change your passwords every 90 days or less.</li>
              <li>Use a combination of letters, numbers, and special characters, with a mix of upper- and lower-case letters to make your passwords more secure.</li>
              <li>Avoid using family names, names of pets, and important dates.</li>
              <li>Avoid using common names or words in foreign languages to create your passwords.</li>
            </ul>

            <h3 className="tc-h4">5. How we're working to keep you safe</h3>
            <ul>
              <li>
                <strong>Security Implementations</strong> — all our systems are secured using
                effective security solutions like firewalls, intrusion detection systems,
                intrusion prevention systems, and anti-malware systems.
              </li>
              <li>
                <strong>Website Certificate</strong> — the SSL Certificate provides clear
                visual indicators of a green address bar. The green address bar/padlock
                verifies that the website belongs to the organization it claims to be from. As
                soon as the website URL address bar becomes green, customers can be assured it
                is a genuine website. Do not log in to the website whose padlock or address bar
                turns red or appears broken.
              </li>
            </ul>
          </Section>
        </PolicyCard>

        <PolicyCard title="Privacy Policy">
          <PrivacyPolicy />
        </PolicyCard>

        <PolicyCard title="Disclosures">
          <DisclosurePage />
        </PolicyCard>

        <PolicyCard title="Legal Disclaimer">
          <LegalisclDaimer />
        </PolicyCard>

        <PolicyCard title="Refund Policy">
          <RefundPolicy />
        </PolicyCard>

        <PolicyCard title="Do's & Don'ts">
          <DoDont />
        </PolicyCard>

        <PolicyCard title="Advertising Disclaimer">
          <AdDisclaimer />
        </PolicyCard>
      </div>
    </div>
  );
}